import React, { useCallback, useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import { ALERT, PALETTE, axisProps, fmtMoney, fmtMoneyShort, fmtNum, tooltipStyle } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Money the hospital is owed: finalized invoices not yet fully paid, aged so the oldest debts
// stand out. Point-in-time - there is no date filter because a debt is a debt until it's paid.

const AGE_BUCKETS = [
  { key: '0-7', label: '0-7 days', test: (d) => d <= 7 },
  { key: '8-30', label: '8-30 days', test: (d) => d <= 30 },
  { key: '31-90', label: '31-90 days', test: (d) => d <= 90 },
  { key: '90+', label: 'Over 90 days', test: () => true },
];

const COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #' },
  { key: 'invoiceDate', label: 'Date' },
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'phone', label: 'Phone' },
  { key: 'grandTotal', label: 'Total', align: 'right', render: (r) => fmtMoney(r.grandTotal) },
  { key: 'paid', label: 'Paid', align: 'right', render: (r) => fmtMoney(r.paid) },
  { key: 'pending', label: 'Pending', align: 'right', render: (r) => <span className="font-semibold text-rose-600">{fmtMoney(r.pending)}</span>, csv: (r) => r.pending },
  { key: 'daysOutstanding', label: 'Days', align: 'right' },
];

const ReceivablesReport = () => {
  const drill = useDrill();
  const fetcher = useCallback(() => reportsAPI.getReceivables(), []);
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Outstanding', value: fmtMoney(k.totalOutstanding ?? 0), tone: Number(k.totalOutstanding) > 0 ? 'alert' : undefined },
    { label: 'Unpaid Invoices', value: fmtNum(k.invoices ?? 0) },
    { label: 'Oldest Debt', value: `${k.oldestDays ?? 0} days` },
    { label: 'Average Age', value: `${k.avgDays ?? 0} days` },
    { label: 'Draft Invoices', value: fmtNum(k.draftInvoices ?? 0), sub: `${fmtMoney(k.draftValue ?? 0)} not yet issued` },
  ];

  const aging = useMemo(() => {
    const rows = data?.rows || [];
    return AGE_BUCKETS.map((b, i) => ({
      bucket: b.label,
      amount: rows
        .filter((r) => {
          const d = Number(r.daysOutstanding);
          return b.test(d) && !AGE_BUCKETS.slice(0, i).some((prev) => prev.test(d));
        })
        .reduce((sum, r) => sum + Number(r.pending), 0),
    }));
  }, [data]);

  return (
    <div className="space-y-5">
      <div className="flex items-center">
        <p className="text-sm text-gray-500">
          As of {data?.range?.to || 'today'}. An invoice appears here once it is finalized and not fully paid.
        </p>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="Aging - how old the outstanding money is">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={aging} margin={{ left: 8, right: 16 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
            <XAxis dataKey="bucket" {...axisProps} />
            <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
            <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
            <Bar dataKey="amount" radius={[4, 4, 0, 0]} barSize={48}>
              {aging.map((entry, i) => (
                <Cell key={entry.bucket} fill={i >= 2 ? ALERT : PALETTE[i]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'invoice', value: r.invoiceNumber })}
        title="Outstanding invoices"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName="receivables"
        emptyText="Nothing outstanding. Every finalized invoice is fully paid."
      />

      <ReportPrintLayout
        title="Receivables"
        subtitle={`As of ${data?.range?.to || ''}`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[{ title: 'Outstanding invoices', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default ReceivablesReport;
